import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Alert,
  Spinner, Modal
} from 'react-bootstrap';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { BASE_URL } from '../../services/api';

// Import icons from react-icons
import { 
  FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, 
  FaGlobe, FaFacebookF, FaTwitter, FaLinkedinIn, 
  FaInstagram, FaYoutube, FaPaperPlane, FaCheckCircle,
  FaExclamationCircle, FaInfoCircle, FaExternalLinkAlt,
  FaWhatsapp, FaCopy, FaMap, FaShareAlt, FaCheck
} from 'react-icons/fa';

const Contact = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeContact, setActiveContact] = useState(null);
  
  // Contact form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    contact_location: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedMap, setSelectedMap] = useState('');
  const [copiedText, setCopiedText] = useState('');
  const [animateElements, setAnimateElements] = useState({});

  const API_BASE_URL = BASE_URL;

  useEffect(() => {
    fetchContacts();
    // Initialize animations
    setTimeout(() => {
      setAnimateElements({
        header: true,
        cards: true,
        map: true
      });
    }, 100);
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/contact_info.php?format_phones=true`);
      
      if (response.data.success) {
        const activeContacts = response.data.data
          .filter(contact => contact.is_active === "1" || contact.is_active === 1)
          .sort((a, b) => a.display_order - b.display_order);
        
        setContacts(activeContacts);
        if (activeContacts.length > 0) {
          setActiveContact(activeContacts[0].id);
          setFormData(prev => ({ ...prev, contact_location: activeContacts[0].id }));
        }
      } else {
        setError('Failed to load contact information');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      // Prepare form data
      const contactName = contacts.find(c => c.id == formData.contact_location)?.name || 'General';
      
      const submissionData = {
        ...formData,
        contact_location: contactName
      };

      const response = await axios.post(
        `${API_BASE_URL}/contact_form.php`,
        submissionData,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.success) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          contact_location: activeContact || ''
        });
        
        // Animation for success message
        setTimeout(() => {
          setSubmitted(false);
        }, 5000);
        
      } else {
        setError(response.data.message || 'Failed to send message. Please try again.');
      }
      
    } catch (err) {
      console.error('Form submission error:', err);
      
      if (err.response) {
        const errorMsg = err.response.data?.message || `Server error: ${err.response.status}`;
        setError(errorMsg);
      } else if (err.request) {
        setError('No response from server. Please check your connection and try again.');
      } else {
        setError('Failed to send message. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleContactSelect = (contactId) => {
    setActiveContact(contactId);
    setFormData(prev => ({ ...prev, contact_location: contactId }));
  };

  const handleViewMap = (embedCode) => {
    let mapSrc = embedCode;
    if (embedCode.includes('<iframe')) {
      const srcMatch = embedCode.match(/src="([^"]+)"/);
      if (srcMatch && srcMatch[1]) {
        mapSrc = srcMatch[1];
      }
    }
    setSelectedMap(mapSrc);
    setShowMapModal(true);
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedText(type);
      setTimeout(() => setCopiedText(''), 2000);
    });
  };

  const getSocialColor = (url) => {
    if (!url) return 'secondary';
    
    if (url.includes('facebook.com')) return 'primary';
    if (url.includes('twitter.com') || url.includes('x.com')) return 'info';
    if (url.includes('linkedin.com')) return 'linkedin';
    if (url.includes('instagram.com')) return 'instagram';
    if (url.includes('youtube.com')) return 'danger';
    return 'secondary';
  };

  const formatFullAddress = (contact) => {
    const parts = [];
    if (contact.address_line1) parts.push(contact.address_line1);
    if (contact.address_line2) parts.push(contact.address_line2);
    if (contact.city && contact.state) {
      parts.push(`${contact.city}, ${contact.state} ${contact.postal_code || ''}`);
    } else if (contact.city) {
      parts.push(contact.city);
    }
    if (contact.country) parts.push(contact.country);
    return parts.join(', ');
  };

  const extractMapSrc = (embedCode) => {
    if (!embedCode) return null;
    
    if (embedCode.includes('<iframe')) {
      const srcMatch = embedCode.match(/src="([^"]+)"/);
      if (srcMatch && srcMatch[1]) {
        return srcMatch[1];
      }
    }
    return embedCode;
  };

  // Format phone for WhatsApp
  const formatPhoneForWhatsApp = (phone) => {
    return phone.replace(/\D/g, '');
  };

  // Animation for interactive elements
  const handleElementHover = (element) => {
    setAnimateElements(prev => ({ ...prev, [element]: true }));
    setTimeout(() => {
      setAnimateElements(prev => ({ ...prev, [element]: false }));
    }, 300);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <div className="edu-loader-animation">
          <div className="edu-loader-orbit edu-orbit-1"></div>
          <div className="edu-loader-orbit edu-orbit-2"></div>
          <div className="edu-loader-orbit edu-orbit-3"></div>
          <div className="edu-loader-center">
            <FaPhone className="edu-loader-icon" />
          </div>
        </div>
        <p className="mt-3 edu-text-shimmer">Loading contact information...</p>
      </Container>
    );
  }

  const renderContactContent = () => {
    if (contacts.length === 0) {
      return (
        <Alert variant="info" className="text-center edu-fade-in">
          <FaInfoCircle className="me-2" />
          No contact information available at the moment.
        </Alert>
      );
    }

    const contact = contacts[0];

    return (
      <>
        {/* Animated Header */}
        <div className={`text-center mb-5 ${animateElements.header ? 'edu-fade-in' : ''}`}>
          <h1 className="display-4 mb-3 edu-text-gradient">Contact Us</h1>
          <p className="lead mb-5 edu-text-float">
            Get in touch with Veda Builders & Promoters for any queries or information
          </p>
        </div>

        {/* Contact Details and Form in Single Row */}
        <Row className="mb-5">
          {/* Contact Details Column with Interactive Buttons */}
          <Col lg={6} className="mb-4">
            <Card className={`shadow-sm h-100 edu-card-float ${animateElements.cards ? 'edu-visible' : ''}`}>
              <Card.Body>
                <Card.Title className="mb-4 edu-text-gradient">
                  <FaMapMarkerAlt className="me-2" />
                  Contact Information
                </Card.Title>
                
                <div className="mb-4">
                  <h4 className="mb-4 edu-text-shimmer">{contact.name}</h4>
                  
                  {/* Address Section with Copy Button */}
                  <div className="mb-4 edu-contact-section">
                    <h6 className="text-muted mb-3">
                      <FaMapMarkerAlt className="me-2" />
                      Address
                    </h6>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <p className="mb-0 edu-text-expand">
                        {formatFullAddress(contact)}
                      </p>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        className="edu-btn-hover"
                        onClick={() => copyToClipboard(formatFullAddress(contact), 'address')}
                      >
                        {copiedText === 'address' ? <FaCheck /> : <FaCopy />}
                      </Button>
                    </div>
                    <Button 
                      variant="primary" 
                      size="sm"
                      className="w-100 edu-btn-glow"
                      onClick={() => {
                        const address = encodeURIComponent(formatFullAddress(contact));
                        window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
                      }}
                      onMouseEnter={() => handleElementHover('addressBtn')}
                    >
                      <FaMapMarkerAlt className="me-2" />
                      Open in Google Maps
                    </Button>
                  </div>

                  {/* Interactive Contact Information */}
                  <div className="mb-4 edu-contact-section">
                    <h6 className="text-muted mb-3">
                      <FaPhone className="me-2" />
                      Contact Information
                    </h6>
                    
                    {/* Phone with Multiple Actions */}
                    {contact.phone_main && (
                      <div className="mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <span className="fw-bold">Phone:</span>
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            className="edu-btn-hover"
                            onClick={() => copyToClipboard(contact.phone_main, 'phone')}
                          >
                            {copiedText === 'phone' ? <FaCheck /> : <FaCopy />}
                          </Button>
                        </div>
                        <div className="d-flex gap-2 mb-2">
                          <Button 
                            variant="outline-success" 
                            size="sm"
                            className="edu-contact-action-btn edu-btn-glow"
                            onClick={() => window.open(`tel:${contact.phone_main.replace(/\D/g, '')}`, '_self')}
                            onMouseEnter={() => handleElementHover('callBtn')}
                          >
                            <FaPhone className="me-2" />
                            Call Now
                          </Button>
                          <Button 
                            variant="outline-success" 
                            size="sm"
                            className="edu-contact-action-btn edu-btn-glow"
                            onClick={() => {
                              const phone = formatPhoneForWhatsApp(contact.phone_main);
                              window.open(`https://wa.me/${phone}`, '_blank');
                            }}
                            onMouseEnter={() => handleElementHover('whatsappBtn')}
                          >
                            <FaWhatsapp className="me-2" />
                            WhatsApp
                          </Button>
                        </div>
                        <p className="text-muted small mb-0 edu-text-float">
                          {contact.phone_main}
                        </p>
                      </div>
                    )}

                    {/* Alternate Phone */}
                    {contact.phone_alternate && (
                      <div className="mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <span className="fw-bold">Alternate:</span>
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            className="edu-btn-hover"
                            onClick={() => copyToClipboard(contact.phone_alternate, 'phoneAlt')}
                          >
                            {copiedText === 'phoneAlt' ? <FaCheck /> : <FaCopy />}
                          </Button>
                        </div>
                        <div className="d-flex gap-2">
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            className="edu-contact-action-btn"
                            onClick={() => window.open(`tel:${contact.phone_alternate.replace(/\D/g, '')}`, '_self')}
                          >
                            <FaPhone className="me-2" />
                            Call
                          </Button>
                        </div>
                        <p className="text-muted small mb-0">
                          {contact.phone_alternate}
                        </p>
                      </div>
                    )}

                    {/* Email with Direct Action */}
                    {contact.email_general && (
                      <div className="mb-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <span className="fw-bold">Email:</span>
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            className="edu-btn-hover"
                            onClick={() => copyToClipboard(contact.email_general, 'email')}
                          >
                            {copiedText === 'email' ? <FaCheck /> : <FaCopy />}
                          </Button>
                        </div>
                        <div className="d-flex gap-2 mb-2">
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            className="edu-contact-action-btn edu-btn-glow"
                            onClick={() => window.open(`mailto:${contact.email_general}`, '_self')}
                            onMouseEnter={() => handleElementHover('emailBtn')}
                          >
                            <FaEnvelope className="me-2" />
                            Send Email
                          </Button>
                        </div>
                        <p className="text-muted small mb-0 edu-text-expand">
                          {contact.email_general}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Hours with Animation */}
                  {(contact.mon_fri_hours || contact.saturday_hours || contact.sunday_hours) && (
                    <div className="mb-4 edu-contact-section">
                      <h6 className="text-muted mb-3">
                        <FaClock className="me-2" />
                        Office Hours
                      </h6>
                      <ul className="list-unstyled edu-list-animate">
                        {contact.mon_fri_hours && (
                          <li className="mb-2 edu-list-item">
                            <strong>Mon - Fri:</strong> {contact.mon_fri_hours}
                          </li>
                        )}
                        {contact.saturday_hours && (
                          <li className="mb-2 edu-list-item">
                            <strong>Saturday:</strong> {contact.saturday_hours}
                          </li>
                        )}
                        {contact.sunday_hours && (
                          <li className="mb-2 edu-list-item">
                            <strong>Sunday:</strong> {contact.sunday_hours}
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  {/* Website */}
                  {contact.website_url && (
                    <div className="mb-4 edu-contact-section">
                      <h6 className="text-muted mb-3">
                        <FaGlobe className="me-2" />
                        Website
                      </h6>
                      <Button 
                        variant="outline-info" 
                        size="sm"
                        className="w-100 edu-btn-hover"
                        onClick={() => window.open(contact.website_url, '_blank')}
                      >
                        <FaGlobe className="me-2" />
                        Visit Website
                      </Button>
                      <p className="text-muted small mt-2">
                        {contact.website_url}
                      </p>
                    </div>
                  )}

                  {/* Animated Social Media */}
                  {(contact.facebook_url || contact.twitter_url || contact.linkedin_url || 
                    contact.instagram_url || contact.youtube_url) && (
                    <div className="mb-3 edu-contact-section">
                      <h6 className="text-muted mb-3">
                        <FaShareAlt className="me-2" />
                        Connect With Us
                      </h6>
                      <div className="d-flex gap-2 edu-social-animate">
                        {contact.facebook_url && (
                          <a 
                            href={contact.facebook_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`btn btn-${getSocialColor(contact.facebook_url)} btn-sm edu-social-btn`}
                          >
                            <FaFacebookF />
                          </a>
                        )}
                        {contact.twitter_url && (
                          <a 
                            href={contact.twitter_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`btn btn-${getSocialColor(contact.twitter_url)} btn-sm edu-social-btn`}
                          >
                            <FaTwitter />
                          </a>
                        )}
                        {contact.linkedin_url && (
                          <a 
                            href={contact.linkedin_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`btn btn-${getSocialColor(contact.linkedin_url)} btn-sm edu-social-btn`}
                          >
                            <FaLinkedinIn />
                          </a>
                        )}
                        {contact.instagram_url && (
                          <a 
                            href={contact.instagram_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`btn btn-${getSocialColor(contact.instagram_url)} btn-sm edu-social-btn`}
                          >
                            <FaInstagram />
                          </a>
                        )}
                        {contact.youtube_url && (
                          <a 
                            href={contact.youtube_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`btn btn-${getSocialColor(contact.youtube_url)} btn-sm edu-social-btn`}
                          >
                            <FaYoutube />
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Contact Form Column with Animations */}
          <Col lg={6} className="mb-4">
            <Card className={`shadow-sm h-100 edu-card-float ${animateElements.cards ? 'edu-visible' : ''}`}>
              <Card.Body>
                <Card.Title className="mb-4 edu-text-gradient">
                  <FaPaperPlane className="me-2" />
                  Send us a Message
                </Card.Title>
                
                {error && (
                  <Alert variant="danger" className="mb-4 edu-alert-shake">
                    <FaExclamationCircle className="me-2" />
                    {error}
                  </Alert>
                )}
                
                {submitted && (
                  <Alert variant="success" className="mb-4 edu-alert-success">
                    <FaCheckCircle className="me-2" />
                    Thank you for your message! We'll get back to you soon.
                  </Alert>
                )}
                
                <Form onSubmit={handleSubmit} className="edu-form-animate">
                  <Form.Group className="mb-3">
                    <Form.Label>Full Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your name"
                      disabled={submitting}
                      className="edu-input-animate"
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email Address *</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder="Enter your email"
                          disabled={submitting}
                          className="edu-input-animate"
                        />
                      </Form.Group>
                    </Col>
                    
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(123) 456-7890"
                          disabled={submitting}
                          className="edu-input-animate"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Subject *</Form.Label>
                    <Form.Control
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter subject"
                      disabled={submitting}
                      className="edu-input-animate"
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Message *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your message"
                      disabled={submitting}
                      className="edu-input-animate edu-textarea-grow"
                    />
                  </Form.Group>

                  <Button 
                    type="submit" 
                    variant="primary" 
                    size="lg"
                    className="w-100 edu-btn-primary edu-btn-glow"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FaPaperPlane className="me-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                  
                  <div className="mt-3 text-center text-muted small edu-text-float">
                    <FaInfoCircle className="me-1" />
                    We'll respond within 24-48 hours
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Large Map Section Below with Animation */}
        <Row className="mb-5">
          <Col lg={12}>
            <Card className={`shadow-sm edu-map-card ${animateElements.map ? 'edu-visible' : ''}`}>
              <Card.Body>
                <Card.Title className="mb-4 edu-text-gradient">
                  <FaMap className="me-2" />
                  Our Location
                </Card.Title>
                
                <div className="mb-3">
                  <h5 className="mb-2 edu-text-shimmer">{contact.name}</h5>
                  <p className="text-muted mb-4 edu-text-float">
                    <FaMapMarkerAlt className="me-2" />
                    {formatFullAddress(contact)}
                  </p>
                </div>
                
                {/* Large Map Display */}
                {contact.google_maps_embed_code ? (
                  <div className="map-container edu-map-animate" style={{ width: '100%', height: '500px', borderRadius: '8px', overflow: 'hidden' }}>
                    <iframe
                      src={extractMapSrc(contact.google_maps_embed_code)}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Google Maps Location"
                    />
                  </div>
                ) : contact.latitude && contact.longitude ? (
                  <div className="map-container edu-map-animate" style={{ width: '100%', height: '500px', borderRadius: '8px', overflow: 'hidden' }}>
                    <iframe
                      src={`https://maps.google.com/maps?q=${contact.latitude},${contact.longitude}&z=15&output=embed`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Google Maps Location"
                    />
                  </div>
                ) : (
                  <Alert variant="info" className="text-center edu-fade-in">
                    <FaMapMarkerAlt className="me-2" />
                    Map location not available
                  </Alert>
                )}
                
                {/* Map Actions */}
                <div className="mt-3 d-flex justify-content-between">
                  <div>
                    {contact.latitude && contact.longitude && (
                      <small className="text-muted edu-text-float">
                        <FaInfoCircle className="me-1" />
                        Coordinates: {contact.latitude}, {contact.longitude}
                      </small>
                    )}
                  </div>
                  <div>
                    {contact.google_maps_embed_code && (
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        className="edu-btn-hover"
                        onClick={() => handleViewMap(contact.google_maps_embed_code)}
                      >
                        <FaExternalLinkAlt className="me-2" />
                        Open Full Screen Map
                      </Button>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | Veda Builders & Promoters</title>
        <meta name="description" content="Get in touch with Veda Builders & Promoters. Find our contact information, location, and send us a message." />
        <meta name="keywords" content="contact, builders, promoters, real estate, construction, inquiry" />
        <meta name="author" content="Veda Builders & Promoters" />
      </Helmet>

      <Container className="py-5 edu-contact-page">
        {renderContactContent()}
      </Container>

      {/* Map Modal */}
      <Modal show={showMapModal} onHide={() => setShowMapModal(false)} size="lg" className="edu-modal">
        <Modal.Header closeButton>
          <Modal.Title>Location Map</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMap ? (
            <div className="map-container edu-map-animate" style={{ width: '100%', height: '500px' }}>
              <iframe
                src={selectedMap}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Google Maps Location"
              />
            </div>
          ) : (
            <div className="text-center py-5 edu-fade-in">
              <FaMap className="fa-3x text-muted mb-3 edu-icon-pulse" />
              <p>Map not available</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMapModal(false)} className="edu-btn-hover">
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Copied Notification */}
      {copiedText && (
        <div className="edu-copied-notification">
          <FaCheck className="me-2" />
          Copied to clipboard!
        </div>
      )}

      {/* Animation Styles */}
      <style jsx="true">{`
        /* Contact Page Animation Styles */
        .edu-contact-page {
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          min-height: 100vh;
        }

        /* Loading Animation */
        .edu-loader-animation {
          position: relative;
          width: 120px;
          height: 120px;
          margin: 0 auto;
        }

        .edu-loader-orbit {
          position: absolute;
          border: 2px dashed rgba(30, 60, 114, 0.3);
          border-radius: 50%;
          animation: eduOrbit 20s linear infinite;
        }

        .edu-orbit-1 {
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        }

        .edu-orbit-2 {
          top: 15px;
          left: 15px;
          right: 15px;
          bottom: 15px;
          animation-direction: reverse;
          animation-duration: 15s;
        }

        .edu-orbit-3 {
          top: 30px;
          left: 30px;
          right: 30px;
          bottom: 30px;
          animation-duration: 10s;
        }

        .edu-loader-center {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 60px;
          height: 60px;
          background: var(--edu-primary, #1e3c72);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 0 30px rgba(30, 60, 114, 0.3);
        }

        .edu-loader-icon {
          font-size: 30px;
          color: white;
          animation: eduPulse 2s ease-in-out infinite;
        }

        @keyframes eduOrbit {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes eduPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }

        /* Text Animations */
        .edu-text-shimmer {
          background: linear-gradient(90deg, #1e3c72, #4a7fba, #1e3c72);
          background-size: 200% 100%;
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: eduShimmer 3s infinite;
        }

        .edu-text-gradient {
          background: linear-gradient(45deg, #1e3c72, #4a7fba);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        @keyframes eduShimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        /* Floating Animations */
        .edu-text-float {
          animation: eduFloat 6s ease-in-out infinite;
        }

        .edu-card-float {
          animation: eduFloatUp 3s ease-in-out infinite;
        }

        @keyframes eduFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        @keyframes eduFloatUp {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }

        /* Fade In Animations */
        .edu-fade-in {
          opacity: 0;
          animation: eduFadeIn 1s ease-out forwards;
        }

        @keyframes eduFadeIn {
          to { opacity: 1; }
        }

        /* Card Animations */
        .edu-card-wrapper {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.6s ease-out;
        }

        .edu-card-wrapper.edu-visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* Button Animations */
        .edu-btn-glow {
          position: relative;
          overflow: hidden;
          box-shadow: 0 0 20px rgba(74, 127, 186, 0.5);
          animation: eduButtonGlow 2s infinite;
        }

        @keyframes eduButtonGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(74, 127, 186, 0.5); }
          50% { box-shadow: 0 0 40px rgba(74, 127, 186, 0.8); }
        }

        .edu-btn-hover {
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .edu-btn-hover:hover {
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 10px 20px rgba(0,0,0,0.15);
        }

        .edu-btn-hover::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          background: rgba(255,255,255,0.2);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          transition: width 0.3s, height 0.3s;
        }

        .edu-btn-hover:hover::after {
          width: 200px;
          height: 200px;
        }

        /* Contact Action Buttons */
        .edu-contact-action-btn {
          flex: 1;
          transition: all 0.3s ease;
          border-radius: 8px;
        }

        .edu-contact-action-btn:hover {
          transform: translateY(-5px);
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
        }

        /* Social Media Animations */
        .edu-social-btn {
          transition: all 0.3s ease;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .edu-social-btn:hover {
          transform: translateY(-5px) rotate(360deg);
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
        }

        .edu-social-animate a {
          animation: eduSocialPop 0.5s ease-out forwards;
          opacity: 0;
        }

        .edu-social-animate a:nth-child(1) { animation-delay: 0.1s; }
        .edu-social-animate a:nth-child(2) { animation-delay: 0.2s; }
        .edu-social-animate a:nth-child(3) { animation-delay: 0.3s; }
        .edu-social-animate a:nth-child(4) { animation-delay: 0.4s; }
        .edu-social-animate a:nth-child(5) { animation-delay: 0.5s; }

        @keyframes eduSocialPop {
          0% { transform: scale(0); opacity: 0; }
          70% { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }

        /* Form Animations */
        .edu-form-animate .form-group {
          opacity: 0;
          animation: eduFormSlide 0.5s ease-out forwards;
        }

        .edu-form-animate .form-group:nth-child(1) { animation-delay: 0.1s; }
        .edu-form-animate .form-group:nth-child(2) { animation-delay: 0.2s; }
        .edu-form-animate .form-group:nth-child(3) { animation-delay: 0.3s; }
        .edu-form-animate .form-group:nth-child(4) { animation-delay: 0.4s; }
        .edu-form-animate .form-group:nth-child(5) { animation-delay: 0.5s; }

        @keyframes eduFormSlide {
          from { opacity: 0; transform: translateX(-30px); }
          to { opacity: 1; transform: translateX(0); }
        }

        .edu-input-animate {
          transition: all 0.3s ease;
          border: 2px solid #e9ecef;
        }

        .edu-input-animate:focus {
          border-color: #4a7fba;
          box-shadow: 0 0 0 0.2rem rgba(74, 127, 186, 0.25);
          transform: translateY(-2px);
        }

        .edu-textarea-grow {
          transition: all 0.3s ease;
        }

        .edu-textarea-grow:focus {
          min-height: 150px;
        }

        /* List Animations */
        .edu-list-animate li {
          opacity: 0;
          transform: translateX(-20px);
          animation: eduListSlide 0.5s ease-out forwards;
        }

        .edu-list-animate li:nth-child(1) { animation-delay: 0.1s; }
        .edu-list-animate li:nth-child(2) { animation-delay: 0.2s; }
        .edu-list-animate li:nth-child(3) { animation-delay: 0.3s; }

        @keyframes eduListSlide {
          to { opacity: 1; transform: translateX(0); }
        }

        /* Alert Animations */
        .edu-alert-shake {
          animation: eduShake 0.5s ease-in-out;
        }

        @keyframes eduShake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }

        .edu-alert-success {
          animation: eduSuccessPop 0.5s ease-out;
        }

        @keyframes eduSuccessPop {
          0% { transform: scale(0.8); opacity: 0; }
          70% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }

        /* Map Animations */
        .edu-map-animate {
          animation: eduMapZoom 1s ease-out;
        }

        @keyframes eduMapZoom {
          from { transform: scale(0.8); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }

        /* Icon Animations */
        .edu-icon-pulse {
          animation: eduPulseIcon 2s ease-in-out infinite;
        }

        @keyframes eduPulseIcon {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        /* Copied Notification */
        .edu-copied-notification {
          position: fixed;
          bottom: 20px;
          right: 20px;
          background: #27ae60;
          color: white;
          padding: 10px 20px;
          border-radius: 8px;
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
          animation: eduNotificationSlide 0.3s ease-out;
          z-index: 9999;
          display: flex;
          align-items: center;
        }

        @keyframes eduNotificationSlide {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        /* Contact Section Animations */
        .edu-contact-section {
          padding: 15px;
          border-radius: 8px;
          background: rgba(248, 249, 250, 0.5);
          margin-bottom: 20px;
          transition: all 0.3s ease;
        }

        .edu-contact-section:hover {
          background: rgba(248, 249, 250, 0.8);
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1);
        }

        /* Text Expand Animation */
        .edu-text-expand {
          transition: all 0.3s ease;
          max-height: 3em;
          overflow: hidden;
        }

        .edu-text-expand:hover {
          max-height: 20em;
        }

        /* Modal Animation */
        .edu-modal .modal-content {
          animation: eduModalSlide 0.3s ease-out;
        }

        @keyframes eduModalSlide {
          from { transform: translateY(-50px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        /* Map Card Animation */
        .edu-map-card {
          opacity: 0;
          transform: translateY(30px);
          animation: eduCardReveal 0.8s ease-out forwards;
          animation-delay: 0.3s;
        }

        @keyframes eduCardReveal {
          to { opacity: 1; transform: translateY(0); }
        }

        /* Primary Button Animation */
        .edu-btn-primary {
          background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
          border: none;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .edu-btn-primary:hover {
          background: linear-gradient(135deg, #2a5298 0%, #1e3c72 100%);
          transform: translateY(-3px);
          box-shadow: 0 10px 20px rgba(30, 60, 114, 0.3);
        }

        .edu-btn-primary::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: 0.5s;
        }

        .edu-btn-primary:hover::before {
          left: 100%;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .edu-contact-action-btn {
            flex-direction: column;
            width: 100%;
            margin-bottom: 10px;
          }
          
          .edu-contact-section {
            padding: 10px;
          }
          
          .edu-social-animate {
            flex-wrap: wrap;
            justify-content: center;
          }
        }

        @media (max-width: 576px) {
          .edu-text-shimmer {
            font-size: 1.2rem;
          }
          
          .edu-contact-page {
            padding: 20px 10px;
          }
        }

        /* Reduced Motion Support */
        @media (prefers-reduced-motion: reduce) {
          .edu-loader-animation,
          .edu-text-shimmer,
          .edu-text-float,
          .edu-card-float,
          .edu-btn-glow,
          .edu-social-btn,
          .edu-form-animate .form-group,
          .edu-list-animate li,
          .edu-alert-shake,
          .edu-alert-success,
          .edu-map-animate,
          .edu-icon-pulse,
          .edu-copied-notification,
          .edu-modal .modal-content,
          .edu-map-card {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default Contact;